/**
 * Adapter: the Firestore accuracy document -> the AccuracyNode AccuracyBox reads.
 *
 * Both shapes describe the same thing; they differ only in optionality. The
 * rollup writes whatever the accuracy-service returned, so every field is
 * `?: T | null` on the way out of Firestore, while accuracyApi's AccuracyCell
 * — written against the HTTP response — has `n`, `n_high_actual` and `n_model`
 * as required numbers.
 *
 * An `as` cast would compile and be wrong the first time the writer drops a
 * field: AccuracyBox would read `undefined` where it expects a number and
 * render NaN rather than an em dash. So the conversion is explicit, and a
 * missing count becomes 0 — which AccuracyBox already renders as "no samples".
 *
 * This exists because the dashboard now has TWO sources for the same metric:
 * Firestore for the fixed scopes the rollup precomputes (today, last complete
 * day, overall and per line), and the direct API for the arbitrary day and
 * arbitrary journey that /analysis/messages needs. This file is that boundary.
 */
import type { AccuracyCell, AccuracyNode } from './accuracyApi';
import type { AccuracyNodeDoc } from '../home/useHomeLive';

const num = (v: number | null | undefined): number => (typeof v === 'number' ? v : 0);
const rate = (v: number | null | undefined): number | null => (typeof v === 'number' ? v : null);

function toCell(c: AccuracyNodeDoc[string] | undefined): AccuracyCell {
  return {
    n:                           num(c?.n),
    class_hit_rate:              rate(c?.class_hit_rate),
    n_high_actual:               num(c?.n_high_actual),
    high_to_low_miss_rate:       rate(c?.high_to_low_miss_rate),
    mae_persons:                 rate(c?.mae_persons),
    n_model:                     num(c?.n_model),
    model_class_hit_rate:        rate(c?.model_class_hit_rate),
    model_high_to_low_miss_rate: rate(c?.model_high_to_low_miss_rate),
  };
}

/** One node ({"N+1".."N+5","all"}). Null in, empty out — AccuracyBox renders "—". */
export function toAccuracyNode(node: AccuracyNodeDoc | null | undefined): AccuracyNode {
  if (!node) return {};
  const out: AccuracyNode = {};
  for (const [horizon, cell] of Object.entries(node)) out[horizon] = toCell(cell);
  return out;
}

/** A whole by_line map. */
export function toAccuracyByLine(
  byLine: Record<string, AccuracyNodeDoc> | null | undefined,
): Record<string, AccuracyNode> {
  if (!byLine) return {};
  const out: Record<string, AccuracyNode> = {};
  for (const [line, node] of Object.entries(byLine)) out[line] = toAccuracyNode(node);
  return out;
}
